import type { ThemeComponents } from '@chakra-ui/react';
import { extendTheme, theme as chakraTheme } from '@chakra-ui/react';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isPlainObject from 'lodash/isPlainObject';

import { EXCLUDED_BASE_THEME_PATHS } from './constants/excluded-base-theme-paths';
import { omitDeep } from './helpers/omit-deep';

type IThemeLike = Record<string, unknown>;

type ICreateThemeParams = {
  name: string;
  tokens: IThemeLike;
  components: ThemeComponents;
};

const asRecord = (value: unknown): IThemeLike => {
  return isPlainObject(value) ? (value as IThemeLike) : {};
};

const resolveThemeValue = <T>(value: T | ((props: object) => T)): T => {
  return isFunction(value) ? value({}) : value;
};

export const cleanBaseTheme = omitDeep(chakraTheme, EXCLUDED_BASE_THEME_PATHS);

export const stripInheritedThemeValues = (
  value: unknown,
  inheritedValue: unknown
): unknown => {
  if (isEqual(value, inheritedValue)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return isEqual(value, inheritedValue) ? undefined : value;
  }

  if (isPlainObject(value)) {
    const valueObject = asRecord(value);
    const inheritedObject = asRecord(inheritedValue);
    const result = Object.entries(valueObject).reduce<IThemeLike>(
      (acc, [key, nestedValue]) => {
        const strippedValue = stripInheritedThemeValues(
          nestedValue,
          inheritedObject[key]
        );

        if (strippedValue !== undefined) {
          acc[key] = strippedValue;
        }

        return acc;
      },
      {}
    );

    return Object.keys(result).length > 0 ? result : undefined;
  }

  return value;
};

export const getEffectiveComponentOverrides = (
  theme: IThemeLike,
  baseTheme: IThemeLike = cleanBaseTheme as IThemeLike
): IThemeLike => {
  const themeComponents = asRecord(theme.components);
  const baseComponents = asRecord(baseTheme.components);

  return Object.entries(themeComponents).reduce<IThemeLike>(
    (acc, [componentName, componentValue]) => {
      const effectiveValue = stripInheritedThemeValues(
        componentValue,
        baseComponents[componentName]
      );

      if (effectiveValue !== undefined) {
        acc[componentName] = effectiveValue;
      }

      return acc;
    },
    {}
  );
};

export const getNormalizedThemeComponentValue = (
  theme: IThemeLike,
  component: string,
  section: 'baseStyle' | 'sizes' | 'variants',
  key?: string,
  baseTheme: IThemeLike = cleanBaseTheme as IThemeLike
): unknown => {
  const themeComponents = asRecord(theme.components);
  const baseComponents = asRecord(baseTheme.components);
  const themeComponent = asRecord(themeComponents[component]);
  const baseComponent = asRecord(baseComponents[component]);
  const themeSection = asRecord(themeComponent[section]);
  const baseSection = asRecord(baseComponent[section]);

  const themeValue = key
    ? resolveThemeValue(themeSection[key])
    : resolveThemeValue(themeComponent[section]);
  const inheritedValue = key
    ? resolveThemeValue(baseSection[key])
    : resolveThemeValue(baseComponent[section]);

  return stripInheritedThemeValues(themeValue, inheritedValue);
};

export const createTheme = ({
  name,
  tokens,
  components,
}: ICreateThemeParams) => {
  return extendTheme(
    {
      name,
      ...tokens,
      components,
    },
    {},
    cleanBaseTheme
  );
};
